import { Fragment, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Loader2, ChevronDown } from 'lucide-react';
import api from '../../lib/api';
import { inr, pct } from './fmt';

const Stat = ({ label, value, testid }) => (
  <div className="rounded-md border border-slate-100 p-2.5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className="text-base font-bold text-slate-900">{value}</div>
  </div>
);

const Money = ({ s }) => (
  <tr className="bg-slate-50/70"><td colSpan={8} className="px-3 py-2">
    <div className="grid md:grid-cols-2 gap-4 text-[11px]" data-testid={`tt-dd-money-${s.student_id}-${s.batch_id}`}>
      <div>
        <div className="font-semibold text-slate-700 mb-1">Invoices</div>
        {s.invoices.length === 0 ? <div className="text-slate-400">No invoices found</div> : s.invoices.map(i => (
          <div key={i.invoice_number} className="text-slate-600">{i.invoice_number} · {i.date} · {inr(i.total)} · balance {inr(i.balance)} · <span className={i.status === 'overdue' ? 'text-rose-600' : i.status === 'paid' ? 'text-emerald-600' : ''}>{i.status}</span></div>
        ))}
      </div>
      <div>
        <div className="font-semibold text-slate-700 mb-1">Payments</div>
        {s.payments.length === 0 ? <div className="text-slate-400">No payments found</div> : s.payments.map(p => (
          <div key={p.payment_number + p.date} className="text-slate-600">#{p.payment_number} · {p.date} · <b>{inr(p.amount)}</b> · {p.payment_mode}{p.invoices.length ? ` · for ${p.invoices.join(', ')}` : ''}</div>
        ))}
      </div>
    </div>
    {s.split > 1 && <p className="text-[10px] text-slate-400 mt-1">Enrolled in {s.split} batches — 1/{s.split} of fees credited here.</p>}
  </td></tr>
);

export const TrainerDrilldown = ({ quarter, trainer, onClose }) => {
  const [d, setD] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState('');
  useEffect(() => { api.get(`/trainer-targets/quarter/${quarter}/trainer/${trainer.trainer_id}`).then(r => setD(r.data)); }, [quarter, trainer.trainer_id]);
  const term = q.trim().toLowerCase();
  const list = (d?.students || []).filter(s => !term || `${s.name} ${s.enrollment_no} ${s.batch_name} ${s.course_name}`.toLowerCase().includes(term));
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="tt-drilldown">
        <DialogHeader><DialogTitle>{trainer.name} · {d?.quarter.label || ''}</DialogTitle></DialogHeader>
        {!d ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Stat label="Multiple" value={`${d.row.multiple}×`} testid="tt-dd-multiple" />
            <Stat label="Target" value={d.row.target ? inr(d.row.target) : 'Not set'} testid="tt-dd-target" />
            <Stat label="Billed" value={`${inr(d.row.billed)} · ${pct(d.row.billed_pct)}`} testid="tt-dd-billed" />
            <Stat label="Collected" value={`${inr(d.row.collected)} · ${pct(d.row.collected_pct)}`} testid="tt-dd-collected" />
            <Stat label="Batches · students" value={`${d.batches.length} · ${d.students.length}`} testid="tt-dd-counts" />
          </div>
          <div className="flex flex-wrap gap-2 text-[11px]" data-testid="tt-dd-batches">
            {d.batches.map(b => <span key={b.id} className="px-2 py-1 rounded bg-slate-100 text-slate-700">{b.name} · {b.course_name} · {b.start_date} · {b.students} students · {inr(b.billed)} billed · {inr(b.collected)} collected</span>)}
          </div>
          {d.students.length === 0 ? <p className="text-sm text-slate-400">No batches started in this quarter.</p> : <>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search student, batch or course" className="h-8 text-xs w-72" data-testid="tt-dd-search" />
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50"><tr>
                <th className="text-left px-3 py-2">Student</th><th className="text-left">Course · Batch</th><th className="text-right">Fee</th><th className="text-right">Paid</th><th className="text-right">Due</th><th className="text-right">Billed credit</th><th className="text-right">Collected credit</th><th />
              </tr></thead>
              <tbody>{list.map(s => {
                const k = `${s.student_id}-${s.batch_id}`;
                return <Fragment key={k}>
                  <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(o => (o === k ? '' : k))} data-testid={`tt-dd-student-${k}`}>
                    <td className="px-3 py-2"><div className="font-medium text-slate-900">{s.name}</div><div className="text-[10px] text-slate-400">{s.enrollment_no} · {s.counsellor}</div></td>
                    <td><div>{s.course_name}</div><div className="text-[10px] text-slate-400">{s.batch_name} · {s.start_date}</div></td>
                    <td className="text-right">{inr(s.total_amount)}</td><td className="text-right text-emerald-700">{inr(s.paid_amount)}</td>
                    <td className={`text-right ${s.due_amount > 0 ? 'text-rose-600' : ''}`}>{inr(s.due_amount)}</td>
                    <td className="text-right font-semibold">{inr(s.billed_credit)}</td><td className="text-right font-semibold">{inr(s.collected_credit)}</td>
                    <td className="px-2"><ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open === k ? 'rotate-180' : ''}`} /></td>
                  </tr>
                  {open === k && <Money s={s} />}
                </Fragment>;
              })}</tbody>
            </table>
          </>}
        </div>}
      </DialogContent>
    </Dialog>
  );
};
